import {ConsecutiveCAPI401sProvider} from '@github-ui/copilot-shared-ui/capi/ConsecutiveCAPI401sContext'
import {AnalyticsProvider} from '@github-ui/analytics-provider'
import {announce} from '@github-ui/aria-live'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {useAnalytics} from '@github-ui/use-analytics'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Banner, Link, SplitPageLayout} from '@primer/react'
import {type FC, type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {FINDINGS_ANALYTICS_EVENTS, FINDINGS_APP_NAME, FINDINGS_CATEGORY} from './analytics'
import {
  getFindingApplicationKey,
  getFindingSuggestionWithPatch,
  getFindingTitle,
  partitionFindings,
} from './entity-helpers'
import {FindingsHeader} from './FindingsHeader'
import {
  FindingsError,
  FindingsLoadingState,
  FindingsNoFindingsState,
  FindingsNotFoundState,
  FindingsProcessingState,
} from './FindingsLifecycleStates'
import {FindingsList} from './FindingsList'
import {FindingsSidebar, type SidebarFixTask} from './FindingsSidebar'
import {FixFindingDialog} from './FixFindingDialog'
import type {FindingResolvedReason, Finding as FindingType} from './types'
import {useDismissibleBanner} from './use-dismissible-banner'
import {useExpandedFindings} from './use-expanded-findings'
import {focusFindingAfterStatusAction, useFindingDeepLink} from './use-finding-deep-link'
import {useFilterFindings} from './use-filter-findings'
import {useFindings} from './use-findings'
import {useFindingsMutations} from './use-findings-mutations'

interface FindingsProps {
  pullRequestBasePath: string
  aliveChannel?: string | null
}

const getSuggestionStateKey = (findingId: string, commentDatabaseId: number) => `${findingId}\0${commentDatabaseId}`

const getSuggestionStates = (findings: FindingType[]) => {
  const states = new Map<string, string>()
  for (const finding of findings) {
    for (const preview of finding.commentPreviews ?? []) {
      if (preview.suggestion && preview.suggestion.diffEntries.length > 0) {
        states.set(getSuggestionStateKey(finding.id, preview.commentDatabaseId), preview.suggestion.state)
      }
    }
  }
  return states
}

const formatSuggestionTransition = (count: number, state: 'applied' | 'outdated') =>
  `${count} ${count === 1 ? 'suggestion' : 'suggestions'} ${state}`

export const Findings: FC<FindingsProps> = props => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  return (
    <AnalyticsProvider appName={FINDINGS_APP_NAME} category={FINDINGS_CATEGORY} metadata={{}}>
      <ErrorBoundary
        boundaryName="Findings"
        onError={e => setErrorMessage(e.message)}
        fallback={<FindingsError error={errorMessage} />}
      >
        <Suspense fallback={<FindingsLoadingState />}>
          <ConsecutiveCAPI401sProvider>
            <FindingsInner {...props} />
          </ConsecutiveCAPI401sProvider>
        </Suspense>
      </ErrorBoundary>
    </AnalyticsProvider>
  )
}

const FindingsInner: FC<FindingsProps> = ({pullRequestBasePath, aliveChannel}) => {
  const {
    findings,
    freshness,
    modelsUrl,
    mergeReadiness,
    showFeedbackBanner,
    feedbackBannerDismissPath,
    isNotFound,
    sweAgentEnabled,
    dotcomChatEnabled,
    headSha,
    canApplySuggestion,
    maxFindingBatchSize,
  } = useFindings({
    pullRequestBasePath,
    aliveChannel,
  })

  const {
    open: openFindings,
    ignored: ignoredFindings,
    outdated: outdatedFindings,
  } = useMemo(() => partitionFindings(findings), [findings])

  const {handleDismiss, handleReopen, actionError} = useFindingsMutations(pullRequestBasePath)

  const freshnessBanner = useDismissibleBanner(freshness !== 'current')
  const actionErrorBanner = useDismissibleBanner(!!actionError)
  const feedbackBanner = useDismissibleBanner(showFeedbackBanner && !!feedbackBannerDismissPath, {
    onDismiss: async () => {
      if (feedbackBannerDismissPath) {
        try {
          await verifiedFetch(feedbackBannerDismissPath, {method: 'POST'})
        } catch {
          // fail silently
        }
      }
    },
  })

  const {displayedFindings: filteredFindings, ...filterProps} = useFilterFindings(
    openFindings,
    ignoredFindings,
    outdatedFindings,
    pullRequestBasePath,
  )
  const [fixFindings, setFixFindings] = useState<FindingType[] | null>(null)
  const [appliedFindingVersions, setAppliedFindingVersions] = useState<ReadonlyMap<string, string>>(() => new Map())
  const pendingAppliedFocusRef = useRef<{findingId: string; applicationKey: string} | null>(null)
  const {expandedFindingIdSet, handleToggleFindingExpanded, handleExpandFinding, handleRemoveExpandedFindings} =
    useExpandedFindings(pullRequestBasePath)

  const {linkedFinding, linkedView, clearLinkedFinding} = useFindingDeepLink({
    openFindings,
    ignoredFindings,
    outdatedFindings,
    onExpandFinding: handleExpandFinding,
    enabled: true,
  })
  const displayedFindings = useMemo(
    () => (linkedFinding ? [linkedFinding] : filteredFindings),
    [filteredFindings, linkedFinding],
  )
  const suggestionStates = useMemo(() => getSuggestionStates(displayedFindings), [displayedFindings])
  const previousSuggestionStatesRef = useRef(suggestionStates)
  const localSuggestionApplicationsRef = useRef(
    new Map<string, {appliedTransitionObserved: boolean; requestSucceeded: boolean}>(),
  )
  const selectedView = linkedView ?? filterProps.selectedView
  const linkedFindingId = linkedFinding?.id ?? null
  const cancelStatusActionFocusRef = useRef<(() => void) | null>(null)
  const focusAfterFindingAction = useCallback((findingId: string) => {
    cancelStatusActionFocusRef.current?.()
    cancelStatusActionFocusRef.current = focusFindingAfterStatusAction(findingId)
  }, [])
  useEffect(
    () => () => {
      cancelStatusActionFocusRef.current?.()
      cancelStatusActionFocusRef.current = null
    },
    [linkedFindingId],
  )

  // handling optimistic updates for fix tasks
  const [fixCcaTaskIds, setFixCcaTaskIds] = useState<Record<string, string>>({})
  const handleTaskCreated = useCallback((ccaTaskId: string, _taskUrl: string, findingIds: string[]) => {
    setFixCcaTaskIds(prev => {
      const next = {...prev}
      for (const id of findingIds) next[id] = ccaTaskId
      return next
    })
  }, [])
  const handleSuggestionApplyStarted = useCallback((finding: FindingType) => {
    const suggestionWithPatch = getFindingSuggestionWithPatch(finding)
    if (suggestionWithPatch) {
      localSuggestionApplicationsRef.current.set(
        getSuggestionStateKey(finding.id, suggestionWithPatch.commentDatabaseId),
        {appliedTransitionObserved: false, requestSucceeded: false},
      )
    }
  }, [])
  const handleSuggestionApplyFailed = useCallback((finding: FindingType) => {
    const suggestionWithPatch = getFindingSuggestionWithPatch(finding)
    if (suggestionWithPatch) {
      const key = getSuggestionStateKey(finding.id, suggestionWithPatch.commentDatabaseId)
      const localApplication = localSuggestionApplicationsRef.current.get(key)
      localSuggestionApplicationsRef.current.delete(key)
      if (localApplication?.appliedTransitionObserved) {
        announce(formatSuggestionTransition(1, 'applied'))
      }
    }
  }, [])
  const handleSuggestionApplied = useCallback(
    (finding: FindingType) => {
      if (!headSha) return

      const suggestionWithPatch = getFindingSuggestionWithPatch(finding)
      if (suggestionWithPatch) {
        const key = getSuggestionStateKey(finding.id, suggestionWithPatch.commentDatabaseId)
        const localApplication = localSuggestionApplicationsRef.current.get(key)
        if (localApplication?.appliedTransitionObserved || !suggestionStates.has(key)) {
          localSuggestionApplicationsRef.current.delete(key)
        } else if (localApplication) {
          localApplication.requestSucceeded = true
        }
      }
      const applicationKey = getFindingApplicationKey(finding, headSha)
      setAppliedFindingVersions(previousVersions => {
        const nextVersions = new Map(previousVersions)
        nextVersions.set(finding.id, applicationKey)
        return nextVersions
      })
      pendingAppliedFocusRef.current = {findingId: finding.id, applicationKey}
    },
    [headSha, suggestionStates],
  )

  useEffect(() => {
    const pendingAppliedFocus = pendingAppliedFocusRef.current
    if (!pendingAppliedFocus) return

    const refreshedFinding = displayedFindings.find(finding => finding.id === pendingAppliedFocus.findingId)
    if (!refreshedFinding) {
      document.getElementById('findings-content')?.focus({preventScroll: true})
      pendingAppliedFocusRef.current = null
      return
    }

    if (headSha && getFindingApplicationKey(refreshedFinding, headSha) !== pendingAppliedFocus.applicationKey) {
      pendingAppliedFocusRef.current = null
    }
  }, [displayedFindings, headSha])

  useEffect(() => {
    let appliedCount = 0
    let outdatedCount = 0

    for (const [key, state] of suggestionStates) {
      const previousState = previousSuggestionStatesRef.current.get(key)
      if (!previousState || previousState === state) continue

      if (state === 'applied') {
        const localApplication = localSuggestionApplicationsRef.current.get(key)
        if (localApplication) {
          if (localApplication.requestSucceeded) {
            localSuggestionApplicationsRef.current.delete(key)
          } else {
            localApplication.appliedTransitionObserved = true
          }
          continue
        }
        appliedCount += 1
      } else if (state === 'outdated') {
        outdatedCount += 1
      }
    }

    for (const [key, localApplication] of localSuggestionApplicationsRef.current) {
      if (localApplication.requestSucceeded && !suggestionStates.has(key)) {
        localSuggestionApplicationsRef.current.delete(key)
      }
    }

    previousSuggestionStatesRef.current = suggestionStates

    const transitions = [
      appliedCount > 0 ? formatSuggestionTransition(appliedCount, 'applied') : null,
      outdatedCount > 0 ? formatSuggestionTransition(outdatedCount, 'outdated') : null,
    ].filter((transition): transition is string => transition !== null)

    if (transitions.length > 0) {
      announce(transitions.join('; '))
    }
  }, [suggestionStates])

  const handleDismissFindings = useCallback(
    async (findingsToDismiss: FindingType[], resolvedReason: FindingResolvedReason) => {
      const linkedFindingToDismiss = findingsToDismiss.find(finding => finding.id === linkedFindingId)
      if (linkedFindingToDismiss) {
        handleExpandFinding(linkedFindingToDismiss.id)
        focusAfterFindingAction(linkedFindingToDismiss.id)
      } else {
        requestAnimationFrame(() => {
          document.getElementById('findings-heading')?.focus({preventScroll: true})
        })
      }

      const didDismiss = await handleDismiss(findingsToDismiss, resolvedReason)
      if (didDismiss) {
        handleRemoveExpandedFindings(
          findingsToDismiss.filter(finding => finding.id !== linkedFindingId).map(finding => finding.id),
        )
      }
    },
    [focusAfterFindingAction, handleDismiss, handleExpandFinding, handleRemoveExpandedFindings, linkedFindingId],
  )
  const handleReopenFindings = useCallback(
    (findingsToReopen: FindingType[]) => {
      const linkedFindingToReopen = findingsToReopen.find(finding => finding.id === linkedFindingId)
      if (linkedFindingToReopen) {
        handleExpandFinding(linkedFindingToReopen.id)
        focusAfterFindingAction(linkedFindingToReopen.id)
      } else {
        requestAnimationFrame(() => {
          document.getElementById('findings-heading')?.focus({preventScroll: true})
        })
      }
      handleReopen(findingsToReopen)
    },
    [focusAfterFindingAction, handleExpandFinding, handleReopen, linkedFindingId],
  )

  const taskBasePath = pullRequestBasePath.replace(/\/pull\/.*$/, '')

  const sidebarFixTasks: SidebarFixTask[] = useMemo(() => {
    const tasks: SidebarFixTask[] = []
    for (const finding of displayedFindings) {
      const ccaTaskId = fixCcaTaskIds[finding.id] ?? finding.ccaTaskId
      if (!ccaTaskId) continue
      tasks.push({
        findingTitle: getFindingTitle(finding),
        ccaTaskId,
        taskBasePath,
      })
    }
    return tasks
  }, [displayedFindings, fixCcaTaskIds, taskBasePath])

  // Announce when findings finish processing (freshness transitions to 'current')
  const previousFreshnessRef = useRef(freshness)
  useEffect(() => {
    if (previousFreshnessRef.current !== 'current' && freshness === 'current') {
      announce('Findings updated')
    }
    previousFreshnessRef.current = freshness
  }, [freshness])

  // Announce when no findings are available (so screen reader users know loading finished)
  useEffect(() => {
    if (isNotFound) {
      announce('No findings available')
    }
  }, [isNotFound])

  // Emit a single impression event when the findings tab is opened (powers
  // active/returning-user metrics on the findings usage dashboard).
  const {sendAnalyticsEvent} = useAnalytics()
  const hasSentViewRef = useRef(false)
  useEffect(() => {
    if (hasSentViewRef.current) return
    hasSentViewRef.current = true
    sendAnalyticsEvent(FINDINGS_ANALYTICS_EVENTS.view.type, FINDINGS_ANALYTICS_EVENTS.view.target, {
      freshness,
      open_count: openFindings.length,
      ignored_count: ignoredFindings.length,
    })
  }, [sendAnalyticsEvent, freshness, openFindings.length, ignoredFindings.length])

  if (isNotFound) {
    return <FindingsNotFoundState />
  }

  const hasNoFindings = openFindings.length === 0 && ignoredFindings.length === 0 && outdatedFindings.length === 0

  // Only take over the whole view with the processing state when we have nothing
  // to show yet. If findings are already present, render them and let the
  // freshness banner convey that more are still being processed.
  if (freshness === 'pending' && hasNoFindings) {
    return <FindingsProcessingState />
  }

  if (hasNoFindings) {
    return <FindingsNoFindingsState />
  }

  return (
    <>
      <FindingsContentContainer>
        <FindingsHeader
          displayedCount={displayedFindings.length}
          handleFix={() => setFixFindings(displayedFindings)}
          sweAgentEnabled={sweAgentEnabled}
        />
        {linkedFinding && (
          <Banner
            variant="info"
            layout="compact"
            className="tmp-mb-3"
            aria-label="Showing linked finding"
            primaryAction={<Banner.PrimaryAction onClick={clearLinkedFinding}>Show all findings</Banner.PrimaryAction>}
          >
            <Banner.Title as="h3">Showing linked finding</Banner.Title>
            <Banner.Description>Filters are temporarily hidden while you view this finding.</Banner.Description>
          </Banner>
        )}
        {freshnessBanner.visible && (
          <Banner
            hideTitle
            variant="info"
            layout="compact"
            className="tmp-mb-3"
            title="Updating findings"
            onDismiss={freshnessBanner.dismiss}
          >
            New findings are being processed and will appear shortly
          </Banner>
        )}
        {actionErrorBanner.visible && (
          <div role="alert">
            <Banner
              hideTitle
              layout="compact"
              variant="warning"
              className="tmp-mb-3"
              title="Action failed"
              onDismiss={actionErrorBanner.dismiss}
            >
              {actionError}
            </Banner>
          </div>
        )}
        {feedbackBanner.visible && (
          <Banner
            hideTitle
            variant="info"
            layout="compact"
            className="tmp-mb-3"
            title="Share feedback on Copilot findings"
            onDismiss={feedbackBanner.dismiss}
          >
            <Banner.Description>
              Help us improve Copilot findings by sharing feedback in{' '}
              <Link inline href="https://github-grid.enterprise.slack.com/archives/C0B6A3D7Y5C" target="_blank">
                Slack
              </Link>
              .
            </Banner.Description>
          </Banner>
        )}
        <FindingsList
          displayedFindings={displayedFindings}
          selectedView={selectedView}
          attentionCount={filterProps.filteredAttentionCount}
          otherCount={filterProps.filteredOtherCount}
          ignoredCount={filterProps.filteredIgnoredCount}
          outdatedCount={filterProps.filteredOutdatedCount}
          expandedFindingIds={expandedFindingIdSet}
          fixCcaTaskIds={fixCcaTaskIds}
          pullRequestBasePath={pullRequestBasePath}
          onViewChange={filterProps.handleViewChange}
          onToggleExpanded={handleToggleFindingExpanded}
          onDismiss={(finding, resolvedReason) => handleDismissFindings([finding], resolvedReason)}
          onReopen={finding => handleReopenFindings([finding])}
          onFix={finding => setFixFindings([finding])}
          sweAgentEnabled={sweAgentEnabled}
          dotcomChatEnabled={dotcomChatEnabled}
          headSha={headSha}
          canApplySuggestion={canApplySuggestion && freshness === 'current'}
          appliedFindingVersions={appliedFindingVersions}
          onSuggestionApplyStarted={handleSuggestionApplyStarted}
          onSuggestionApplyFailed={handleSuggestionApplyFailed}
          onSuggestionApplied={handleSuggestionApplied}
        />
        <p className="text-center color-fg-muted text-small mt-3">
          <Link
            className="Link--inTextBlock"
            href="https://docs.github.com/en/copilot/responsible-use-of-github-copilot-features/responsible-use-of-github-copilot-code-review"
            inline
            muted
          >
            Copilot
          </Link>
          {' uses AI. Check for mistakes.'}
        </p>
      </FindingsContentContainer>
      {!linkedFinding && (
        <SplitPageLayout.Pane position="end" sticky divider="none" padding="condensed" width="large">
          <FindingsSidebar
            selectedView={filterProps.selectedView}
            attentionCount={filterProps.attentionCount}
            otherCount={filterProps.otherCount}
            ignoredCount={filterProps.ignoredCount}
            outdatedCount={filterProps.outdatedCount}
            onViewChange={filterProps.handleViewChange}
            availableSources={filterProps.availableSources}
            selectedSources={filterProps.selectedSources}
            onSourceToggle={filterProps.handleSourceToggle}
            availableTools={filterProps.availableTools}
            selectedTools={filterProps.selectedTools}
            onToolToggle={filterProps.handleToolToggle}
            fixTasks={sidebarFixTasks}
            mergeReadiness={mergeReadiness}
          />
        </SplitPageLayout.Pane>
      )}
      {fixFindings && headSha && (
        <FixFindingDialog
          findings={fixFindings}
          basePath={pullRequestBasePath}
          headSha={headSha}
          maxFindingBatchSize={maxFindingBatchSize}
          modelsUrl={modelsUrl}
          onClose={() => setFixFindings(null)}
          onTaskCreated={handleTaskCreated}
        />
      )}
    </>
  )
}

const FindingsContentContainer: FC<{children: ReactNode}> = ({children}) => (
  <SplitPageLayout.Content as="div" padding="none">
    {children}
  </SplitPageLayout.Content>
)
